import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { BtnLoader } from "src/commonLoader/index";
import { ManageTableModal } from "src/components/ManageTableModal/ManageTableModal";
import {
  delete_table_modal,
  search_table,
  set_table_loding,
  set_tables,
} from "src/store/features/restuarant-table-slice";
import { GRAPHQL_ENDPOINT, GUEST_ORDER_BASE_URL } from "src/constants/endpoints";
import {
  GET_CUSTOMER_ORDER_FOR_EMPLOYEE,
  GET_TABLES,
} from "src/constants/queries";
import { QRCode } from "react-qrcode-logo";
import { SAVE_TABLE_LAYOUTS } from "src/constants/mutations";
import { useAuth } from "src/contexts/auth-provider";
import { useAxiosInterceptor } from "src/hooks/useAxiosInterceptor";
import useOutsideClick from "src/hooks/useOutsideClick";
import TableOrderMOdal from "src/components/TableOrderModal/TableOrderModal";
import { failureToast, successToast } from "src/utils/toasts";
import { ReportPageLayout } from "src/features/Reports/components/ReportPageLayout";
import { ReportTabs } from "src/features/Reports/components/ReportTabs";
import QRCodeLib from "qrcode";
import { jsPDF } from "jspdf";
import {
  defaultTableFootprint,
  effectiveLayoutWidthHeight,
  normalizeWidthHeightForShape,
  resolveLayoutShape,
  type TableLayoutShape,
} from "src/utils/tableLayoutGeometry";

const BORDER_LIGHT = "rgba(0,0,0,0.06)";

type ActiveTab = "list" | "layout" | "guestOrdering";

function getTableQrUrl(publicToken: string | null | undefined): string | null {
  if (!publicToken) return null;
  const base = GUEST_ORDER_BASE_URL.replace(/\/$/, "");
  return `${base}/g/${publicToken}`;
}

const QR_CARD_BRAND_ORANGE = "#f97316";

/** Proportional vertical rhythm (px at scale 1). Small gap in title block, larger gap before QR, equal above/below QR. */
const QR_CARD_GAP_AFTER_BAR = 32;
const QR_CARD_GAP_AFTER_EATSBITS = 16;
const QR_CARD_GAP_AFTER_TABLE = 12;
const QR_CARD_GAP_ABOVE_QR = 40;
const QR_CARD_GAP_BELOW_QR = 40;
const QR_CARD_OPTICAL_QR_SHIFT = 8; // Move QR down to correct for left-heavy visual weight

/** Draw a printable styled QR card (accent bar, brand, table label, instruction, QR). No footer URL. */
function getStyledQrCardDataUrl(qrCanvas: HTMLCanvasElement, tableNumber: number): string {
  const scale = 2;
  const w = 400 * scale;
  const padding = 24 * scale;
  const radius = 20 * scale;
  // Thinner accent bar (~40% less height) so it feels intentional, not structural; TABLE number stays the star
  const accentBarHeight = 3 * scale;
  // Side margins so the bar has breathing room and reads as a premium accent, not a page border
  const accentBarSideMargin = 0.12 * w;
  const qrSize = Math.round(220 * 1.06 * scale); // 5–8% larger: fills vertical balance, more grounded
  const card = document.createElement("canvas");
  const eatBitsFont = 11 * scale;
  const tableFont = 28 * scale;
  const subtitleFont = 13 * scale;

  const gap1 = QR_CARD_GAP_AFTER_BAR * scale;
  const gap2 = QR_CARD_GAP_AFTER_EATSBITS * scale;
  const gap3 = QR_CARD_GAP_AFTER_TABLE * scale;
  const gap4 = QR_CARD_GAP_ABOVE_QR * scale;
  const bottomPad = QR_CARD_GAP_BELOW_QR * scale;
  const opticalShift = QR_CARD_OPTICAL_QR_SHIFT * scale;

  let y = accentBarHeight + gap1 + eatBitsFont + gap2 + tableFont + gap3 + subtitleFont + gap4 + opticalShift;
  const h = y + qrSize + bottomPad;
  card.width = w;
  card.height = h;

  const ctx = card.getContext("2d");
  if (!ctx) return qrCanvas.toDataURL("image/png");

  const r = radius;
  ctx.beginPath();
  ctx.moveTo(r, 0);
  ctx.lineTo(w - r, 0);
  ctx.quadraticCurveTo(w, 0, w, r);
  ctx.lineTo(w, h - r);
  ctx.quadraticCurveTo(w, h, w - r, h);
  ctx.lineTo(r, h);
  ctx.quadraticCurveTo(0, h, 0, h - r);
  ctx.lineTo(0, r);
  ctx.quadraticCurveTo(0, 0, r, 0);
  ctx.closePath();
  ctx.fillStyle = "#ffffff";
  ctx.fill();

  ctx.fillStyle = QR_CARD_BRAND_ORANGE;
  const barX = accentBarSideMargin;
  const barW = w - 2 * accentBarSideMargin;
  ctx.fillRect(barX, 0, barW, accentBarHeight);

  y = accentBarHeight + gap1 + eatBitsFont;
  ctx.fillStyle = "#9ca3af";
  ctx.font = `${eatBitsFont}px sans-serif`;
  ctx.textAlign = "center";
  ctx.fillText("EATSBITS", w / 2, y);
  y += gap2 + tableFont;

  // TABLE + number: tighter letter-spacing on "TABLE " so it feels less wide; block stays centered
  const tableLabel = "TABLE ";
  const numLabel = String(tableNumber);
  ctx.font = `bold ${tableFont}px sans-serif`;
  const numWidth = ctx.measureText(numLabel).width;
  const tracking = -1.2 * scale;
  let tableWidth = 0;
  for (let i = 0; i < tableLabel.length; i++) {
    tableWidth += ctx.measureText(tableLabel[i]).width + (i < tableLabel.length - 1 ? tracking : 0);
  }
  const startX = w / 2 - (tableWidth + numWidth) / 2;
  let x = startX;
  ctx.fillStyle = "#374151";
  ctx.textAlign = "left";
  for (let i = 0; i < tableLabel.length; i++) {
    const c = tableLabel[i];
    ctx.fillText(c, x, y);
    x += ctx.measureText(c).width + (i < tableLabel.length - 1 ? tracking : 0);
  }
  ctx.fillStyle = QR_CARD_BRAND_ORANGE;
  ctx.fillText(numLabel, x, y);
  ctx.textAlign = "center";
  y += gap3 + subtitleFont;

  ctx.fillStyle = "#8A94A6";
  ctx.font = `${subtitleFont}px sans-serif`;
  ctx.fillText("Scan to order & pay", w / 2, y);
  y += gap4 + opticalShift;

  const qrX = (w - qrSize) / 2;
  ctx.drawImage(qrCanvas, 0, 0, qrCanvas.width, qrCanvas.height, qrX, y, qrSize, qrSize);

  return card.toDataURL("image/png");
}

export interface TableLayoutItem {
  correlationId: string;
  tableNumber: number;
  tableSize: number;
  section: number | null;
  status: string;
  currentOrderCorrelationId?: string | null;
  x: number;
  y: number;
  width?: number;
  height?: number;
  rotation?: number;
  /** Resolved after load / merge; layout metadata only (not derived from seat count). */
  shape: TableLayoutShape;
}

// ----- List View: Table row action menu -----
function TableRowActionsMenu({
  onClose,
  table,
  onEdit,
  onDelete,
}: {
  onClose: () => void;
  table: any;
  onEdit: (table: any) => void;
  onDelete: (table: any) => void;
}) {
  return (
    <div
      className="absolute right-0 top-full mt-1 z-20 min-w-[128px] py-1 bg-white rounded-xl border border-[#F1F1F2] shadow-[0_4px_12px_rgba(0,0,0,0.08)] overflow-hidden"
      role="menu"
    >
      <button
        type="button"
        className="w-full text-left px-4 py-2.5 text-sm text-[#828282] hover:bg-[#f1f1f2] hover:text-[#252525]"
        onClick={() => {
          onEdit(table);
          onClose();
        }}
      >
        Edit
      </button>
      <button
        type="button"
        className="w-full text-left px-4 py-2.5 text-sm text-[#828282] hover:bg-red-50 hover:text-red-600"
        onClick={() => {
          onDelete(table);
          onClose();
        }}
      >
        Delete
      </button>
    </div>
  );
}

function TableRowActions({
  table,
  onEdit,
  onDelete,
}: {
  table: any;
  onEdit: (table: any) => void;
  onDelete: (table: any) => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useOutsideClick({ ref, handler: () => setOpen(false) });

  return (
    <div
      ref={ref}
      className="relative flex justify-end"
      onClick={(e) => e.stopPropagation()}
    >
      <button
        type="button"
        className="h-10 min-h-[40px] w-10 min-w-[40px] flex items-center justify-center rounded-[10px] text-gray-400 hover:text-gray-600 active:bg-gray-100"
        aria-label="Row actions"
        onClick={() => setOpen((o) => !o)}
      >
        <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor" aria-hidden>
          <circle cx="10" cy="4" r="1.5" />
          <circle cx="10" cy="10" r="1.5" />
          <circle cx="10" cy="16" r="1.5" />
        </svg>
      </button>
      {open && (
        <TableRowActionsMenu
          table={table}
          onClose={() => setOpen(false)}
          onEdit={onEdit}
          onDelete={onDelete}
        />
      )}
    </div>
  );
}

function getStatusPillClass(status: string) {
  if (status === "AVAILABLE") return "bg-[#e6f7ec] text-[#16a34a]";
  if (status === "IN_USE") return "bg-[#e6f0fb] text-[#2563eb]";
  if (status === "RESERVED") return "bg-[#fff1e6] text-[#f97316]";
  return "bg-gray-100 text-gray-600";
}

function getStatusLabel(status: string) {
  if (status === "AVAILABLE") return "Available";
  if (status === "IN_USE") return "In use";
  if (status === "RESERVED") return "Reserved";
  return status;
}

/** List column: persisted layout shape (same resolve rules as Layout tab when a layout row exists). */
function layoutShapeTypeLabel(table: {
  layout?: { shape?: string | null; width?: number | null; height?: number | null } | null;
  tableSize?: number | null;
}): string {
  const L = table.layout;
  if (L == null) return "—";
  const eff = effectiveLayoutWidthHeight(L.width, L.height, table.tableSize ?? 4);
  const shape = resolveLayoutShape(L.shape, eff.width, eff.height);
  if (shape === "CIRCLE") return "Circle";
  if (shape === "SQUARE") return "Square";
  return "Rectangle";
}

const RestaurantTable = () => {
  const dispatch = useDispatch();
  const { axBe } = useAxiosInterceptor();
  const { hasRestaurant } = useAuth();
  const [activeTab, setActiveTab] = useState<ActiveTab>("list");
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editTableModal, setEditTableModal] = useState<any>(null);
  const [statusFilter, setStatusFilter] = useState<"" | "AVAILABLE" | "IN_USE" | "RESERVED">("");
  type TableSortKey = "tableNumber" | "tableSize" | "section";
  const [sortKey, setSortKey] = useState<TableSortKey | null>(null);
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");
  const [isShowTableOrderModal, setisShowTableOrderModal] = useState<any>(null);
  const [tableOrderDetails] = useState<any>(null);
  const [activeOrders, setActiveOrders] = useState<any[]>([]);
  const [timerTick, setTimerTick] = useState(0);
  // Layout View state
  const [layoutItems, setLayoutItems] = useState<TableLayoutItem[]>([]);
  const [layoutBySection, setLayoutBySection] = useState<Record<string, TableLayoutItem[]>>({});
  const [selectedLayoutSection, setSelectedLayoutSection] = useState<number | null>(null);
  const [layoutEditMode, setLayoutEditMode] = useState(false);
  const [layoutSaving, setLayoutSaving] = useState(false);
  const [selectedLayoutItemCorrelationId, setSelectedLayoutItemCorrelationId] = useState<string | null>(null);
  const [viewQrTable, setViewQrTable] = useState<any>(null);
  const [draggingTableId, setDraggingTableId] = useState<string | null>(null);
  const [hoveredTableId, setHoveredTableId] = useState<string | null>(null);
  const [zoomLevel, setZoomLevel] = useState(1);
  const [isPanning, setIsPanning] = useState(false);
  const draggingRef = useRef<{ id: string; startX: number; startY: number; itemX: number; itemY: number } | null>(null);
  const panningRef = useRef<{ startX: number; startY: number; startScrollLeft: number; startScrollTop: number } | null>(null);
  const canvasRef = useRef<HTMLDivElement>(null);
  const viewportRef = useRef<HTMLDivElement>(null);
  const canvasSizeRef = useRef({ w: 1600, h: 1200 });
  const zoomLevelRef = useRef(1);
  const lastSavedBySectionRef = useRef<Record<string, string>>({});
  const lastValidLayoutRef = useRef<Record<string, { layoutX: number; layoutY: number }>>({});
  /** When true, overlap guard in constrainLayoutPosition is skipped so tables can be separated after bad/overlapping placements. */
  const layoutDragEscapeOverlapRef = useRef(false);
  const viewQrModalQrRef = useRef<HTMLDivElement>(null);
  const hiddenExportQrRef = useRef<HTMLDivElement>(null);
  const [downloadTableToken, setDownloadTableToken] = useState<{ publicToken: string; tableNumber: number } | null>(null);
  const [downloadAllQrsPdfLoading, setDownloadAllQrsPdfLoading] = useState(false);

  const LAYOUT_RAIL_GRID_STEP = 12;
  const LAYOUT_RAIL_SNAP_THRESHOLD = 10; // snap when within this many px of a guide
  const LAYOUT_RAIL_SNAP_CLOSE_TO_GUIDE = 10; // also snap when already this close to a guide (even if moving faster)
  const LAYOUT_RAIL_SNAP_MAX_POINTER_DELTA = 18; // apply guide snap when moving slowly (px per frame)
  const LAYOUT_RAIL_RELEASE_GUIDE_THRESHOLD = 10; // on release, snap to guide if within this many px
  const LAYOUT_RAIL_MIN_TABLE_GAP = 12;
  const LAYOUT_RAIL_CANVAS_PADDING = 12;
  const CANVAS_W_MIN = 1600;
  const CANVAS_H_MIN = 1200;

  const [canvasSize, setCanvasSize] = useState({ w: CANVAS_W_MIN, h: CANVAS_H_MIN });

  zoomLevelRef.current = zoomLevel;

  useEffect(() => {
    const update = () => {
      const zoom = Math.max(0.5, Math.min(2, zoomLevelRef.current));
      const winW = typeof window !== "undefined" ? window.innerWidth : CANVAS_W_MIN;
      const winH = typeof window !== "undefined" ? window.innerHeight : CANVAS_H_MIN;
      const el = viewportRef.current;
      const elW = el?.clientWidth ?? 0;
      const elH = el?.clientHeight ?? 0;
      const viewW = elW || winW;
      const viewH = elH || winH;
      const w = Math.max(CANVAS_W_MIN, Math.ceil(viewW / zoom));
      const h = Math.max(CANVAS_H_MIN, Math.ceil(viewH / zoom));
      canvasSizeRef.current = { w, h };
      setCanvasSize({ w, h });
    };
    update();
    const el = viewportRef.current;
    if (el) {
      const ro = new ResizeObserver(update);
      ro.observe(el);
      window.addEventListener("resize", update);
      const t = setTimeout(update, 150);
      return () => {
        ro.disconnect();
        window.removeEventListener("resize", update);
        clearTimeout(t);
      };
    }
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, [activeTab, zoomLevel]);

  const { filteredTables, isTableLoading } = useSelector((state: any) => state.restaurantTable);

  const GRID_STEP = 180;
  const sectionKey = (s: number | null) => (s === null ? "__none__" : String(s));
  const layoutSections = useMemo(() => {
    const set = new Set<number | null>();
    (filteredTables || []).forEach((t: any) => set.add(t.section ?? null));
    return Array.from(set).sort((a, b) => (a === null ? -1 : b === null ? 1 : (a ?? 0) - (b ?? 0)));
  }, [filteredTables]);

  // Stable signature so seed effect doesn't run when filteredTables is a new reference with same data
  const tablesSignature = useMemo(() => {
    const list = filteredTables || [];
    const ids = list.map((t: any) => t.correlationId).sort().join(",");
    const sections = Array.from(new Set(list.map((t: any) => String(t.section ?? "__none__")))).sort().join(",");
    return (list.length ?? 0) + "-" + ids + "-" + sections;
  }, [filteredTables]);

  const displayTables = statusFilter
    ? filteredTables.filter((t: any) => t.status === statusFilter)
    : filteredTables;

  const sortedDisplayTables = useMemo(() => {
    if (!sortKey) return displayTables;
    const order = sortOrder === "asc" ? 1 : -1;
    return [...displayTables].sort((a: any, b: any) => {
      const aVal = a[sortKey];
      const bVal = b[sortKey];
      const aNum = sortKey === "section" ? (aVal != null && aVal !== "" ? Number(aVal) : NaN) : Number(aVal);
      const bNum = sortKey === "section" ? (bVal != null && bVal !== "" ? Number(bVal) : NaN) : Number(bVal);
      const aSort = Number.isNaN(aNum) ? (sortOrder === "asc" ? Infinity : -Infinity) : aNum;
      const bSort = Number.isNaN(bNum) ? (sortOrder === "asc" ? Infinity : -Infinity) : bNum;
      return (aSort - bSort) * order;
    });
  }, [displayTables, sortKey, sortOrder]);

  const handleSort = (key: TableSortKey) => {
    if (sortKey === key) {
      setSortOrder((o) => (o === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortOrder("asc");
    }
  };

  const searchTable = (value: string) => {
    dispatch(search_table(value));
  };

  const handleDeleteClick = (correlationId: string) => {
    dispatch(delete_table_modal(correlationId));
  };

  const openEditModal = (table: any) => {
    setEditTableModal(table);
  };

  const getTables = useCallback(async () => {
    dispatch(set_table_loding(true));
    try {
      const { data } = await axBe.post(GRAPHQL_ENDPOINT, {
        query: GET_TABLES,
        variables: {
          storeCorrelationId: hasRestaurant.correlationId,
        },
      });
      dispatch(
        set_tables({
          tables: data?.data?.storeTablesByStoreCorrelationId || [],
        })
      );
      setTimeout(() => dispatch(set_table_loding(false)), 500);
    } catch (error) {
      setTimeout(() => dispatch(set_table_loding(false)), 500);
      console.error(error);
    }
  }, [axBe, dispatch, hasRestaurant?.correlationId]);

  const magnageTableOrder = (table: any) => {
    setEditTableModal(table);
  };

  const getOrders = useCallback(async () => {
    if (!hasRestaurant?.correlationId) return;
    try {
      const { data } = await axBe.post(GRAPHQL_ENDPOINT, {
        query: GET_CUSTOMER_ORDER_FOR_EMPLOYEE,
        variables: {
          storeCorrelationId: hasRestaurant.correlationId,
        },
      });
      // Support both standard GraphQL shape (data.data.queryName) and unwrapped (data.queryName)
      const orders =
        data?.data?.activeCustomerOrdersByStoreCorrelationId ??
        data?.activeCustomerOrdersByStoreCorrelationId ??
        [];
      setActiveOrders(Array.isArray(orders) ? orders : []);
    } catch (error) {
      console.error(error);
    }
  }, [axBe, hasRestaurant?.correlationId]);

  useEffect(() => {
    getTables();
    getOrders();
  }, [getOrders, getTables]);

  const ordersByCorrelationId = useMemo(() => {
    const m = new Map<string, any>();
    (activeOrders || []).forEach((o: any) => {
      if (o?.correlationId) m.set(o.correlationId, o);
    });
    return m;
  }, [activeOrders]);

  useEffect(() => {
    const interval = setInterval(() => setTimerTick((t) => t + 1), 1000);
    return () => clearInterval(interval);
  }, []);

  // When downloadTableToken is set, hidden QR is rendered; after paint, export styled PNG and clear.
  useEffect(() => {
    if (!downloadTableToken) return;
    const id = setTimeout(() => {
      const canvas = hiddenExportQrRef.current?.querySelector("canvas");
      if (canvas) {
        const a = document.createElement("a");
        a.download = `table-${downloadTableToken.tableNumber}-qr.png`;
        a.href = getStyledQrCardDataUrl(canvas, downloadTableToken.tableNumber);
        a.click();
      }
      setDownloadTableToken(null);
    }, 150);
    return () => clearTimeout(id);
  }, [downloadTableToken]);

  const downloadAllQrsPdf = useCallback(async () => {
    const tables = (filteredTables || [])
      .filter((t: any) => t?.publicToken && getTableQrUrl(t.publicToken))
      .sort((a: any, b: any) => (a.tableNumber ?? 0) - (b.tableNumber ?? 0));
    if (tables.length === 0) {
      failureToast("No tables with QR codes to download.");
      return;
    }
    setDownloadAllQrsPdfLoading(true);
    try {
      // High-resolution QR (800px) so it stays sharp when scaled down in the styled card and in PDF
      const QR_GEN_SIZE = 800;
      const canvas = document.createElement("canvas");
      canvas.width = QR_GEN_SIZE;
      canvas.height = QR_GEN_SIZE;

      const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
      const pageW = doc.internal.pageSize.getWidth();
      const pageH = doc.internal.pageSize.getHeight();

      // Styled card has same layout as download PNG: accent bar, EATSBITS, TABLE N, "Scan to order & pay", QR.
      // Card pixel size: 800×876 (w × h). QR in card is 466px. We want QR = 50mm in PDF.
      const qrSizeMm = 50;
      const cardWpx = 800;
      const cardHpx = 876;
      const qrInCardPx = Math.round(220 * 1.06 * 2); // same as getStyledQrCardDataUrl
      const imgW = (qrSizeMm * cardWpx) / qrInCardPx;
      const imgH = (imgW * cardHpx) / cardWpx;

      for (let i = 0; i < tables.length; i++) {
        const table = tables[i];
        const url = getTableQrUrl(table.publicToken)!;
        const tableNumber = table.tableNumber ?? 0;

        if (i > 0) doc.addPage();

        // Generate high-res QR, then same styled card as download link (with table number and text)
        await new Promise<void>((resolve, reject) => {
          QRCodeLib.toCanvas(canvas, url, { width: QR_GEN_SIZE, margin: 2 }, (err) =>
            err ? reject(err) : resolve()
          );
        });
        const dataUrl = getStyledQrCardDataUrl(canvas, tableNumber);

        // Center card on page
        const x = (pageW - imgW) / 2;
        const y = (pageH - imgH) / 2;
        doc.addImage(dataUrl, "PNG", x, y, imgW, imgH);
      }

      doc.save("tables-qr-codes.pdf");
      successToast("PDF downloaded.");
    } catch (e) {
      console.error(e);
      failureToast("Failed to generate PDF.");
    } finally {
      setDownloadAllQrsPdfLoading(false);
    }
  }, [filteredTables]);

  const formatElapsed = (createdAt: string | number | null | undefined) => {
    if (createdAt == null) return "";
    const start = typeof createdAt === "string" ? new Date(createdAt).getTime() : createdAt;
    const elapsed = Math.floor((Date.now() - start) / 1000);
    const h = Math.floor(elapsed / 3600);
    const m = Math.floor((elapsed % 3600) / 60);
    const s = elapsed % 60;
    if (h > 0) return `${h}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
    return `${m}:${String(s).padStart(2, "0")}`;
  };

  const formatCents = (cents: number | null | undefined) => {
    if (cents == null) return "";
    return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(cents / 100);
  };

  // Build layout by section from API: use table.layout (x, y, width, height, rotation, shape) when present, else default grid
  const buildLayoutItemsForSection = useCallback(
    (section: number | null, tables: any[]): TableLayoutItem[] => {
      const list = tables.filter((t: any) => (t.section ?? null) === section);
      return list.map((table: any, index: number) => {
        const L = table.layout;
        const row = Math.floor(index / 4);
        const col = index % 4;
        const x = L?.x ?? 24 + col * GRID_STEP;
        const y = L?.y ?? 24 + row * GRID_STEP;
        const { width: effW, height: effH } = effectiveLayoutWidthHeight(
          L?.width,
          L?.height,
          table.tableSize ?? 4
        );
        const shape = resolveLayoutShape(L?.shape, effW, effH);
        const { width: nw, height: nh } = normalizeWidthHeightForShape(shape, effW, effH);
        return {
          correlationId: table.correlationId,
          tableNumber: table.tableNumber,
          tableSize: table.tableSize,
          section: section,
          status: table.status,
          currentOrderCorrelationId: table.currentOrderCorrelationId ?? null,
          x,
          y,
          width: nw,
          height: nh,
          rotation: L?.rotation,
          shape,
        };
      });
    },
    [GRID_STEP]
  );

  const prevTablesSignatureRef = useRef<string | null>(null);

  // Seed layoutBySection from filteredTables when on Layout tab; merge with existing to preserve local edits
  useEffect(() => {
    if (activeTab !== "layout" || !filteredTables?.length) return;
    const tablesChanged = prevTablesSignatureRef.current !== tablesSignature;
    if (tablesChanged) prevTablesSignatureRef.current = tablesSignature;

    setLayoutBySection((prev) => {
      const next: Record<string, TableLayoutItem[]> = {};
      layoutSections.forEach((sec) => {
        const key = sectionKey(sec);
        const fresh = buildLayoutItemsForSection(sec, filteredTables);
        const existingById = new Map((prev[key] ?? []).map((p) => [p.correlationId, p]));
        next[key] = fresh.map((item) => {
          const existing = existingById.get(item.correlationId);
          if (existing)
            return {
              ...item,
              x: existing.x,
              y: existing.y,
              width: existing.width ?? item.width,
              height: existing.height ?? item.height,
              rotation: existing.rotation ?? item.rotation,
              shape: existing.shape ?? item.shape,
            };
          return item;
        });
      });
      // Only update "last saved" when table data actually changed (e.g. from API), not when only section selection changed
      if (tablesChanged) {
        Object.keys(next).forEach((k) => {
          lastSavedBySectionRef.current[k] = JSON.stringify(next[k]);
        });
      }
      return { ...prev, ...next };
    });
    if (layoutSections.length && !layoutSections.includes(selectedLayoutSection)) {
      setSelectedLayoutSection(layoutSections[0]);
    }
  }, [activeTab, tablesSignature, buildLayoutItemsForSection, selectedLayoutSection]);

  // When selectedLayoutSection or layoutBySection changes, show current section's items
  useEffect(() => {
    if (activeTab !== "layout") return;
    setLayoutItems(layoutBySection[sectionKey(selectedLayoutSection)] ?? []);
  }, [activeTab, selectedLayoutSection, layoutBySection]);

  /** Matches desktop TableNode: effective stored dims, then resolve + normalize (layout units = CSS px). */
  const getItemLayoutSize = (item: TableLayoutItem) => {
    const eff = effectiveLayoutWidthHeight(item.width, item.height, item.tableSize ?? 4);
    const resolved = resolveLayoutShape(item.shape, eff.width, eff.height);
    return normalizeWidthHeightForShape(resolved, eff.width, eff.height);
  };
  const getItemW = (item: TableLayoutItem) => getItemLayoutSize(item).width;
  const getItemH = (item: TableLayoutItem) => getItemLayoutSize(item).height;

  const visualTopLeftToLayout = (
    vx: number,
    vy: number,
    w: number,
    h: number,
    rotationDeg: number
  ): { layoutX: number; layoutY: number } => {
    const r = ((rotationDeg % 360) + 360) % 360;
    if (r === 90 || r === 270) {
      return { layoutX: vx - (w - h) / 2, layoutY: vy + (w - h) / 2 };
    }
    return { layoutX: vx, layoutY: vy };
  };

  const layoutToVisualTopLeft = (
    layoutX: number,
    layoutY: number,
    w: number,
    h: number,
    rotationDeg: number
  ): { vx: number; vy: number } => {
    const r = ((rotationDeg % 360) + 360) % 360;
    if (r === 90 || r === 270) {
      return { vx: layoutX + (w - h) / 2, vy: layoutY - (w - h) / 2 };
    }
    return { vx: layoutX, vy: layoutY };
  };

  /** Expanded AABB overlap in layout space (matches constrainLayoutPosition). */
  const layoutItemExpandedOverlapsOther = useCallback((self: TableLayoutItem, other: TableLayoutItem) => {
    const w = getItemW(self);
    const h = getItemH(self);
    const rotDeg = self.rotation ?? 0;
    const rotRad = (rotDeg * Math.PI) / 180;
    const r = ((rotDeg % 360) + 360) % 360;
    const rotated90 = r === 90 || r === 270;
    const { layoutX: candLx, layoutY: candLy } = visualTopLeftToLayout(self.x, self.y, w, h, rotDeg);
    const vx = rotated90 ? candLx + (w - h) / 2 : candLx;
    const vy = rotated90 ? candLy - (w - h) / 2 : candLy;
    const nx = rotated90 ? vx - (w - h) / 2 : vx;
    const ny = rotated90 ? vy + (w - h) / 2 : vy;
    const aabbW = w * Math.abs(Math.cos(rotRad)) + h * Math.abs(Math.sin(rotRad));
    const aabbH = w * Math.abs(Math.sin(rotRad)) + h * Math.abs(Math.cos(rotRad));
    const inflate = LAYOUT_RAIL_MIN_TABLE_GAP / 2;
    const cx = nx + w / 2;
    const cy = ny + h / 2;
    const thisMinX = cx - aabbW / 2 - inflate;
    const thisMaxX = cx + aabbW / 2 + inflate;
    const thisMinY = cy - aabbH / 2 - inflate;
    const thisMaxY = cy + aabbH / 2 + inflate;

    const ow = getItemW(other);
    const oh = getItemH(other);
    const or = (other.rotation ?? 0) * (Math.PI / 180);
    const oAabbW = ow * Math.abs(Math.cos(or)) + oh * Math.abs(Math.sin(or));
    const oAabbH = ow * Math.abs(Math.sin(or)) + oh * Math.abs(Math.cos(or));
    const { layoutX: oLx, layoutY: oLy } = visualTopLeftToLayout(other.x, other.y, ow, oh, other.rotation ?? 0);
    const oCx = oLx + ow / 2;
    const oCy = oLy + oh / 2;
    const oMinX = oCx - oAabbW / 2 - inflate;
    const oMaxX = oCx + oAabbW / 2 + inflate;
    const oMinY = oCy - oAabbH / 2 - inflate;
    const oMaxY = oCy + oAabbH / 2 + inflate;
    return thisMinX < oMaxX && thisMaxX > oMinX && thisMinY < oMaxY && thisMaxY > oMinY;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- getItemW/H + v2l layout math is stable for this screen
  }, []);

  const constrainLayoutPosition = useCallback(
    (
      node: TableLayoutItem,
      candidateLayoutX: number,
      candidateLayoutY: number,
      otherItems: TableLayoutItem[],
      canvasW: number,
      canvasH: number,
      pointerMoveDistancePx?: number,
      skipGridSnap?: boolean
    ): { layoutX: number; layoutY: number } => {
      const nodeId = node.correlationId;
      const lastValid = lastValidLayoutRef.current[nodeId] ?? {
        layoutX: visualTopLeftToLayout(node.x, node.y, getItemW(node), getItemH(node), node.rotation ?? 0).layoutX,
        layoutY: visualTopLeftToLayout(node.x, node.y, getItemW(node), getItemH(node), node.rotation ?? 0).layoutY,
      };
      const w = getItemW(node);
      const h = getItemH(node);
      const rotDeg = node.rotation ?? 0;
      const rotRad = (rotDeg * Math.PI) / 180;
      const r = ((rotDeg % 360) + 360) % 360;
      const rotated90 = r === 90 || r === 270;
      let vx = rotated90 ? candidateLayoutX + (w - h) / 2 : candidateLayoutX;
      let vy = rotated90 ? candidateLayoutY - (w - h) / 2 : candidateLayoutY;
      const visW = rotated90 ? h : w;
      const visH = rotated90 ? w : h;
      const halfVisW = visW / 2;
      const halfVisH = visH / 2;
      const guideX = new Set<number>([0, canvasW]);
      const guideY = new Set<number>([0, canvasH]);
      otherItems.forEach((other) => {
        if (other.correlationId === nodeId) return;
        const ow = getItemW(other);
        const oh = getItemH(other);
        const or = (other.rotation ?? 0) * (Math.PI / 180);
        const oAabbW = ow * Math.abs(Math.cos(or)) + oh * Math.abs(Math.sin(or));
        const oAabbH = ow * Math.abs(Math.sin(or)) + oh * Math.abs(Math.cos(or));
        const { layoutX: oLx, layoutY: oLy } = visualTopLeftToLayout(other.x, other.y, ow, oh, other.rotation ?? 0);
        const oCx = oLx + ow / 2;
        const oCy = oLy + oh / 2;
        guideX.add(oCx - oAabbW / 2);
        guideX.add(oCx);
        guideX.add(oCx + oAabbW / 2);
        guideY.add(oCy - oAabbH / 2);
        guideY.add(oCy);
        guideY.add(oCy + oAabbH / 2);
      });
      // Only apply guide snap when moving slowly AND already close to a guide (so slow drag in empty space can move the table)
      let minDistToGuideX = Infinity;
      let minDistToGuideY = Infinity;
      guideX.forEach((g) => {
        minDistToGuideX = Math.min(
          minDistToGuideX,
          Math.abs(vx - g),
          Math.abs(vx + halfVisW - g),
          Math.abs(vx + visW - g)
        );
      });
      guideY.forEach((g) => {
        minDistToGuideY = Math.min(
          minDistToGuideY,
          Math.abs(vy - g),
          Math.abs(vy + halfVisH - g),
          Math.abs(vy + visH - g)
        );
      });
      const isMovingSlowly =
        pointerMoveDistancePx == null ||
        pointerMoveDistancePx <= LAYOUT_RAIL_SNAP_MAX_POINTER_DELTA;
      const isCloseToGuide =
        minDistToGuideX <= LAYOUT_RAIL_SNAP_THRESHOLD ||
        minDistToGuideY <= LAYOUT_RAIL_SNAP_THRESHOLD;
      // Skip guide snap on very small movements so the table can break free from a guide
      const isMicroMove = pointerMoveDistancePx != null && pointerMoveDistancePx < 4;
      const useGuideSnap = isMovingSlowly && isCloseToGuide && !isMicroMove;
      if (useGuideSnap) {
        let bestDx = LAYOUT_RAIL_SNAP_THRESHOLD + 1;
        guideX.forEach((g) => {
          const dLeft = Math.abs(vx - g);
          const dCenter = Math.abs(vx + halfVisW - g);
          const dRight = Math.abs(vx + visW - g);
          if (dLeft <= LAYOUT_RAIL_SNAP_THRESHOLD && dLeft < bestDx) { bestDx = dLeft; vx = g; }
          if (dCenter <= LAYOUT_RAIL_SNAP_THRESHOLD && dCenter < bestDx) { bestDx = dCenter; vx = g - halfVisW; }
          if (dRight <= LAYOUT_RAIL_SNAP_THRESHOLD && dRight < bestDx) { bestDx = dRight; vx = g - visW; }
        });
        let bestDy = LAYOUT_RAIL_SNAP_THRESHOLD + 1;
        guideY.forEach((g) => {
          const dTop = Math.abs(vy - g);
          const dCenter = Math.abs(vy + halfVisH - g);
          const dBottom = Math.abs(vy + visH - g);
          if (dTop <= LAYOUT_RAIL_SNAP_THRESHOLD && dTop < bestDy) { bestDy = dTop; vy = g; }
          if (dCenter <= LAYOUT_RAIL_SNAP_THRESHOLD && dCenter < bestDy) { bestDy = dCenter; vy = g - halfVisH; }
          if (dBottom <= LAYOUT_RAIL_SNAP_THRESHOLD && dBottom < bestDy) { bestDy = dBottom; vy = g - visH; }
        });
      }
      let nx = rotated90 ? vx - (w - h) / 2 : vx;
      let ny = rotated90 ? vy + (w - h) / 2 : vy;
      const aabbW = w * Math.abs(Math.cos(rotRad)) + h * Math.abs(Math.sin(rotRad));
      const aabbH = w * Math.abs(Math.sin(rotRad)) + h * Math.abs(Math.cos(rotRad));
      const inflate = LAYOUT_RAIL_MIN_TABLE_GAP / 2;
      let cx = nx + w / 2;
      let cy = ny + h / 2;
      const thisMinX = cx - aabbW / 2 - inflate;
      const thisMaxX = cx + aabbW / 2 + inflate;
      const thisMinY = cy - aabbH / 2 - inflate;
      const thisMaxY = cy + aabbH / 2 + inflate;
      if (!layoutDragEscapeOverlapRef.current) {
        for (const other of otherItems) {
          if (other.correlationId === nodeId) continue;
          const ow = getItemW(other);
          const oh = getItemH(other);
          const or = (other.rotation ?? 0) * (Math.PI / 180);
          const oAabbW = ow * Math.abs(Math.cos(or)) + oh * Math.abs(Math.sin(or));
          const oAabbH = ow * Math.abs(Math.sin(or)) + oh * Math.abs(Math.cos(or));
          const { layoutX: oLx, layoutY: oLy } = visualTopLeftToLayout(other.x, other.y, ow, oh, other.rotation ?? 0);
          const oCx = oLx + ow / 2;
          const oCy = oLy + oh / 2;
          const oMinX = oCx - oAabbW / 2 - inflate;
          const oMaxX = oCx + oAabbW / 2 + inflate;
          const oMinY = oCy - oAabbH / 2 - inflate;
          const oMaxY = oCy + oAabbH / 2 + inflate;
          if (thisMinX < oMaxX && thisMaxX > oMinX && thisMinY < oMaxY && thisMaxY > oMinY) {
            lastValidLayoutRef.current[nodeId] = lastValid;
            return lastValid;
          }
        }
      }
      const clampCxMin = LAYOUT_RAIL_CANVAS_PADDING + aabbW / 2;
      const clampCxMax = canvasW - LAYOUT_RAIL_CANVAS_PADDING - aabbW / 2;
      const clampCyMin = LAYOUT_RAIL_CANVAS_PADDING + aabbH / 2;
      const clampCyMax = canvasH - LAYOUT_RAIL_CANVAS_PADDING - aabbH / 2;
      cx = nx + w / 2;
      cy = ny + h / 2;
      cx = Math.max(clampCxMin, Math.min(clampCxMax, cx));
      cy = Math.max(clampCyMin, Math.min(clampCyMax, cy));
      nx = cx - w / 2;
      ny = cy - h / 2;
      let finalX: number;
      let finalY: number;
      if (skipGridSnap) {
        finalX = nx;
        finalY = ny;
      } else {
        let vxGrid = rotated90 ? nx + (w - h) / 2 : nx;
        let vyGrid = rotated90 ? ny - (w - h) / 2 : ny;
        vxGrid = Math.round(vxGrid / LAYOUT_RAIL_GRID_STEP) * LAYOUT_RAIL_GRID_STEP;
        vyGrid = Math.round(vyGrid / LAYOUT_RAIL_GRID_STEP) * LAYOUT_RAIL_GRID_STEP;
        finalX = rotated90 ? vxGrid - (w - h) / 2 : vxGrid;
        finalY = rotated90 ? vyGrid + (w - h) / 2 : vyGrid;
      }
      const result = { layoutX: finalX, layoutY: finalY };
      lastValidLayoutRef.current[nodeId] = result;
      return result;
    },
    []
  );

  // Drag handlers for Layout View
  const handleLayoutPointerDown = useCallback(
    (e: React.PointerEvent, item: TableLayoutItem) => {
      if (!layoutEditMode) return;
      e.preventDefault();
      const secList = layoutBySection[sectionKey(selectedLayoutSection)] ?? [];
      layoutDragEscapeOverlapRef.current = secList.some(
        (o) => o.correlationId !== item.correlationId && layoutItemExpandedOverlapsOther(item, o)
      );
      setSelectedLayoutItemCorrelationId(item.correlationId);
      setDraggingTableId(item.correlationId);
      const w = getItemW(item);
      const h = getItemH(item);
      const { layoutX, layoutY } = visualTopLeftToLayout(item.x, item.y, w, h, item.rotation ?? 0);
      lastValidLayoutRef.current[item.correlationId] = { layoutX, layoutY };
      draggingRef.current = {
        id: item.correlationId,
        startX: e.clientX,
        startY: e.clientY,
        itemX: item.x,
        itemY: item.y,
      };
      (e.target as HTMLElement).setPointerCapture?.(e.pointerId);
    },
    [layoutEditMode, layoutBySection, selectedLayoutSection, layoutItemExpandedOverlapsOther]
  );

  const handleCanvasBackgroundPointerDown = useCallback(
    (e: React.PointerEvent) => {
      if (!layoutEditMode) return;
      if ((e.target as HTMLElement).closest("[data-table-card]")) return;
      if (!viewportRef.current) return;
      e.preventDefault();
      panningRef.current = {
        startX: e.clientX,
        startY: e.clientY,
        startScrollLeft: viewportRef.current.scrollLeft,
        startScrollTop: viewportRef.current.scrollTop,
      };
      setIsPanning(true);
      (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    },
    [layoutEditMode]
  );

  const handleLayoutWheel = useCallback((e: React.WheelEvent) => {
    if (!e.ctrlKey && !e.metaKey) return;
    e.preventDefault();
    setZoomLevel((prev) => Math.min(2, Math.max(0.5, prev - e.deltaY * 0.002)));
  }, []);

  const zoomIn = useCallback(() => {
    setZoomLevel((prev) => Math.min(2, prev + 0.25));
  }, []);
  const zoomOut = useCallback(() => {
    setZoomLevel((prev) => Math.max(0.5, prev - 0.25));
  }, []);
  const zoomReset = useCallback(() => {
    setZoomLevel(1);
  }, []);
  const resetView = useCallback(() => {
    setZoomLevel(1);
    if (viewportRef.current) {
      viewportRef.current.scrollLeft = 0;
      viewportRef.current.scrollTop = 0;
    }
  }, []);

  const handleRotate90 = useCallback(() => {
    if (!selectedLayoutItemCorrelationId) return;
    const key = sectionKey(selectedLayoutSection);
    setLayoutItems((prev) =>
      prev.map((p) =>
        p.correlationId === selectedLayoutItemCorrelationId
          ? { ...p, rotation: ((p.rotation ?? 0) + 90) % 360 }
          : p
      )
    );
    setLayoutBySection((lb) => ({
      ...lb,
      [key]: (lb[key] ?? []).map((p) =>
        p.correlationId === selectedLayoutItemCorrelationId
          ? { ...p, rotation: ((p.rotation ?? 0) + 90) % 360 }
          : p
      ),
    }));
  }, [selectedLayoutItemCorrelationId, selectedLayoutSection]);

  const handleApplyLayoutShape = useCallback(
    (nextShape: TableLayoutShape) => {
      if (!selectedLayoutItemCorrelationId) return;
      const key = sectionKey(selectedLayoutSection);
      const upd = (items: TableLayoutItem[]) =>
        items.map((p) => {
          if (p.correlationId !== selectedLayoutItemCorrelationId) return p;
          const { width: w, height: h } = effectiveLayoutWidthHeight(
            p.width,
            p.height,
            p.tableSize ?? 4
          );
          const { width, height } = normalizeWidthHeightForShape(nextShape, w, h);
          return { ...p, shape: nextShape, width, height };
        });
      setLayoutItems(upd);
      setLayoutBySection((lb) => ({ ...lb, [key]: upd(lb[key] ?? []) }));
    },
    [selectedLayoutItemCorrelationId, selectedLayoutSection]
  );

  const handleLayoutPointerMove = useCallback(
    (e: React.PointerEvent) => {
      const pan = panningRef.current;
      if (pan && viewportRef.current) {
        viewportRef.current.scrollLeft = pan.startScrollLeft + (pan.startX - e.clientX);
        viewportRef.current.scrollTop = pan.startScrollTop + (pan.startY - e.clientY);
        return;
      }
      const d = draggingRef.current;
      if (!d) return;
      const dx = e.clientX - d.startX;
      const dy = e.clientY - d.startY;
      const candidateVx = d.itemX + dx;
      const candidateVy = d.itemY + dy;
      const key = sectionKey(selectedLayoutSection);
      const { w: canvasW, h: canvasH } = canvasSizeRef.current;

      setLayoutItems((prev) => {
        const node = prev.find((p) => p.correlationId === d.id);
        if (!node) return prev;
        const w = getItemW(node);
        const h = getItemH(node);
        const { layoutX: candLx, layoutY: candLy } = visualTopLeftToLayout(
          candidateVx,
          candidateVy,
          w,
          h,
          node.rotation ?? 0
        );
        const others = prev.filter((p) => p.correlationId !== d.id);
        const pointerMovePx = Math.hypot(dx, dy);
        const { layoutX: finalLx, layoutY: finalLy } = constrainLayoutPosition(
          node,
          candLx,
          candLy,
          others,
          canvasW,
          canvasH,
          pointerMovePx,
          true
        );
        const { vx: finalVx, vy: finalVy } = layoutToVisualTopLeft(finalLx, finalLy, w, h, node.rotation ?? 0);
        const next = prev.map((p) =>
          p.correlationId === d.id ? { ...p, x: finalVx, y: finalVy } : p
        );
        setLayoutBySection((lb) => ({ ...lb, [key]: next }));
        draggingRef.current = {
          ...d,
          startX: e.clientX,
          startY: e.clientY,
          itemX: finalVx,
          itemY: finalVy,
        };
        return next;
      });
    },
    [selectedLayoutSection, constrainLayoutPosition]
  );

  // On release: snap to a guide if within threshold, otherwise snap to grid (keeps alignment from being undone)
  const getSnappedReleasePosition = useCallback(
    (
      node: TableLayoutItem,
      otherItems: TableLayoutItem[],
      canvasW: number,
      canvasH: number
    ): { vx: number; vy: number } => {
      const w = getItemW(node);
      const h = getItemH(node);
      const rot = node.rotation ?? 0;
      const r = ((rot % 360) + 360) % 360;
      const rotated90 = r === 90 || r === 270;
      const visW = rotated90 ? h : w;
      const visH = rotated90 ? w : h;
      const halfVisW = visW / 2;
      const halfVisH = visH / 2;
      let vx = node.x;
      let vy = node.y;
      const guideX = new Set<number>([0, canvasW]);
      const guideY = new Set<number>([0, canvasH]);
      otherItems.forEach((other) => {
        if (other.correlationId === node.correlationId) return;
        const ow = getItemW(other);
        const oh = getItemH(other);
        const or = (other.rotation ?? 0) * (Math.PI / 180);
        const oAabbW = ow * Math.abs(Math.cos(or)) + oh * Math.abs(Math.sin(or));
        const oAabbH = ow * Math.abs(Math.sin(or)) + oh * Math.abs(Math.cos(or));
        const { layoutX: oLx, layoutY: oLy } = visualTopLeftToLayout(other.x, other.y, ow, oh, other.rotation ?? 0);
        const oCx = oLx + ow / 2;
        const oCy = oLy + oh / 2;
        guideX.add(oCx - oAabbW / 2);
        guideX.add(oCx);
        guideX.add(oCx + oAabbW / 2);
        guideY.add(oCy - oAabbH / 2);
        guideY.add(oCy);
        guideY.add(oCy + oAabbH / 2);
      });
      let snappedVx: number | null = null;
      let bestDx = LAYOUT_RAIL_RELEASE_GUIDE_THRESHOLD + 1;
      guideX.forEach((g) => {
        const dLeft = Math.abs(vx - g);
        const dCenter = Math.abs(vx + halfVisW - g);
        const dRight = Math.abs(vx + visW - g);
        if (dLeft <= LAYOUT_RAIL_RELEASE_GUIDE_THRESHOLD && dLeft < bestDx) {
          bestDx = dLeft;
          snappedVx = g;
        }
        if (dCenter <= LAYOUT_RAIL_RELEASE_GUIDE_THRESHOLD && dCenter < bestDx) {
          bestDx = dCenter;
          snappedVx = g - halfVisW;
        }
        if (dRight <= LAYOUT_RAIL_RELEASE_GUIDE_THRESHOLD && dRight < bestDx) {
          bestDx = dRight;
          snappedVx = g - visW;
        }
      });
      let snappedVy: number | null = null;
      let bestDy = LAYOUT_RAIL_RELEASE_GUIDE_THRESHOLD + 1;
      guideY.forEach((g) => {
        const dTop = Math.abs(vy - g);
        const dCenter = Math.abs(vy + halfVisH - g);
        const dBottom = Math.abs(vy + visH - g);
        if (dTop <= LAYOUT_RAIL_RELEASE_GUIDE_THRESHOLD && dTop < bestDy) {
          bestDy = dTop;
          snappedVy = g;
        }
        if (dCenter <= LAYOUT_RAIL_RELEASE_GUIDE_THRESHOLD && dCenter < bestDy) {
          bestDy = dCenter;
          snappedVy = g - halfVisH;
        }
        if (dBottom <= LAYOUT_RAIL_RELEASE_GUIDE_THRESHOLD && dBottom < bestDy) {
          bestDy = dBottom;
          snappedVy = g - visH;
        }
      });
      const { layoutX, layoutY } = visualTopLeftToLayout(node.x, node.y, w, h, rot);
      const gx = Math.round(layoutX / LAYOUT_RAIL_GRID_STEP) * LAYOUT_RAIL_GRID_STEP;
      const gy = Math.round(layoutY / LAYOUT_RAIL_GRID_STEP) * LAYOUT_RAIL_GRID_STEP;
      const { vx: gridVx, vy: gridVy } = layoutToVisualTopLeft(gx, gy, w, h, rot);
      return {
        vx: snappedVx ?? gridVx,
        vy: snappedVy ?? gridVy,
      };
    },
    []
  );

  const handleLayoutPointerUp = useCallback(
    (e: React.PointerEvent) => {
      if (panningRef.current) {
        panningRef.current = null;
        setIsPanning(false);
        (e.currentTarget as HTMLElement).releasePointerCapture?.(e.pointerId);
        return;
      }
      const d = draggingRef.current;
      draggingRef.current = null;
      setDraggingTableId(null);
      layoutDragEscapeOverlapRef.current = false;
      (e.target as HTMLElement).releasePointerCapture?.(e.pointerId);
      if (d) {
        const key = sectionKey(selectedLayoutSection);
        const { w: canvasW, h: canvasH } = canvasSizeRef.current;
        setLayoutItems((prev) => {
          const node = prev.find((p) => p.correlationId === d.id);
          if (!node) return prev;
          const others = prev.filter((p) => p.correlationId !== d.id);
          const { vx, vy } = getSnappedReleasePosition(node, others, canvasW, canvasH);
          return prev.map((p) => (p.correlationId === d.id ? { ...p, x: vx, y: vy } : p));
        });
        setLayoutBySection((lb) => {
          const list = lb[key] ?? [];
          const node = list.find((p) => p.correlationId === d.id);
          if (!node) return lb;
          const others = list.filter((p) => p.correlationId !== d.id);
          const { vx, vy } = getSnappedReleasePosition(node, others, canvasW, canvasH);
          return { ...lb, [key]: list.map((p) => (p.correlationId === d.id ? { ...p, x: vx, y: vy } : p)) };
        });
      }
    },
    [selectedLayoutSection, getSnappedReleasePosition]
  );

  const layoutDirty =
    JSON.stringify(layoutItems) !== (lastSavedBySectionRef.current[sectionKey(selectedLayoutSection)] ?? "");

  const handleSaveLayout = useCallback(async () => {
    if (!hasRestaurant?.correlationId) return;
    setLayoutSaving(true);
    try {
      const storeCorrelationId = hasRestaurant.correlationId;
      const normalizeItemDims = (item: TableLayoutItem): TableLayoutItem => {
        const { width: w, height: h } = effectiveLayoutWidthHeight(
          item.width,
          item.height,
          item.tableSize ?? 4
        );
        const { width, height } = normalizeWidthHeightForShape(item.shape, w, h);
        return { ...item, width, height };
      };
      const normalizedBySection: Record<string, TableLayoutItem[]> = Object.fromEntries(
        Object.entries(layoutBySection).map(([k, list]) => [k, list.map(normalizeItemDims)])
      );
      const saveTableLayoutsInput = Object.values(normalizedBySection).flat().map((item) => {
        const row: Record<string, unknown> = {
          tableCorrelationId: item.correlationId,
          storeCorrelationId,
          x: item.x,
          y: item.y,
          width: item.width,
          height: item.height,
          shape: item.shape,
        };
        if (item.rotation != null) row.rotation = item.rotation;
        return row;
      });
      await axBe.post(GRAPHQL_ENDPOINT, {
        query: SAVE_TABLE_LAYOUTS,
        variables: { saveTableLayoutsInput },
      });
      setLayoutBySection(normalizedBySection);
      Object.keys(normalizedBySection).forEach((k) => {
        lastSavedBySectionRef.current[k] = JSON.stringify(normalizedBySection[k]);
      });
      successToast("Layout saved!");
      setLayoutEditMode(false);
      getTables();
    } catch (err) {
      failureToast("Failed to save layout");
    } finally {
      setLayoutSaving(false);
    }
  }, [hasRestaurant?.correlationId, layoutBySection, axBe, getTables]);

  const layoutBorderClass = (status: string) => {
    if (status === "AVAILABLE") return "border border-[#22c55e]";
    if (status === "IN_USE") return "border border-[#ef4444]";
    if (status === "RESERVED") return "border-2 border-[#f97316]";
    return "border border-[#e5e7eb]";
  };

  const layoutFillClass = (status: string) => {
    if (status === "AVAILABLE") return "bg-[#f7fef9]";
    if (status === "IN_USE") return "bg-[#fef2f2]";
    if (status === "RESERVED") return "bg-[#fff7ed]";
    return "bg-white";
  };

  const layoutStatusPillClass = (status: string) => {
    if (status === "AVAILABLE") return "bg-[#f0fdf4] text-[#166534]";
    if (status === "IN_USE") return "bg-[#fef2f2] text-[#7f1d1d]";
    if (status === "RESERVED") return "bg-[#fff7ed] text-[#ea580c]";
    return "bg-gray-100 text-gray-600";
  };

  const layoutStatusLabel = (status: string) => {
    if (status === "IN_USE") return "In use";
    if (status === "AVAILABLE") return "Available";
    if (status === "RESERVED") return "Reserved";
    return status;
  };

  return (
    <ReportPageLayout
      compactTop
      toolbarMarginTop={0}
      toolbarMarginBottom={12}
      toolbar={
        <div className="flex items-center justify-between gap-4 w-full min-w-0">
          <ReportTabs
            tabs={[
              { id: "list", label: "List" },
              { id: "layout", label: "Layout" },
              { id: "guestOrdering", label: "Guest Ordering" },
            ]}
            activeTabId={activeTab}
            onSelect={(id) => setActiveTab(id as ActiveTab)}
            inline
          />
        </div>
      }
    >
      {activeTab === "list" && (
        <div className="w-full flex-1 min-w-0 flex flex-col">
          <div
            className="bg-white rounded-xl shadow-[0_4px_12px_rgba(0,0,0,0.04)] overflow-hidden flex-1 flex flex-col min-h-0"
            style={{ border: `1px solid ${BORDER_LIGHT}` }}
          >
            <section
              className="shrink-0 pt-1.5 pb-1.5 px-3 border-b flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between sm:gap-3"
              style={{ borderColor: BORDER_LIGHT }}
              aria-label="Tables toolbar"
            >
              {/* Mobile: search + Add Table on one row; sm+: search only (fixed width) */}
              <div className="flex flex-row gap-2 items-center min-w-0 w-full sm:w-[260px] sm:shrink-0">
                <div className="relative flex-1 min-w-0">
                  <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                    <svg
                      className="w-4 h-4 text-gray-500"
                      aria-hidden="true"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 20 20"
                    >
                      <path
                        stroke="currentColor"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="m19 19-4-4m0-7A7 7 0 1 1 1 8a7 7 0 0 1 14 0Z"
                      />
                    </svg>
                  </div>
                  <input
                    type="search"
                    id="default-search"
                    className="block border border-[#e5e7eb] bg-white w-full h-9 pl-9 pr-3 text-sm text-black rounded-lg focus:outline-none focus:border-[#f97316] focus:shadow-[0_0_0_3px_rgba(249,115,22,0.15)]"
                    placeholder="Search Table..."
                    onChange={(e) => searchTable(e.target.value)}
                  />
                </div>
                <button
                  type="button"
                  className="inline-flex h-9 items-center justify-center px-4 text-sm font-semibold leading-none text-white bg-orange rounded-lg shrink-0 transition-[transform,box-shadow] duration-[120ms] ease-out hover:translate-y-[-1px] hover:shadow-[0_8px_20px_rgba(0,0,0,0.10)] active:translate-y-0 active:shadow-[0_4px_12px_rgba(0,0,0,0.08)] sm:hidden"
                  onClick={() => setShowCreateModal(true)}
                >
                  Add Table
                </button>
              </div>
              {/* Mobile: filters only; sm+: filters + Add Table */}
              <div className="flex items-center gap-2 flex-wrap sm:flex-nowrap sm:gap-3 sm:justify-end min-w-0 sm:flex-1">
                <span className="text-[13px] text-[#6b7280] shrink-0">Filter:</span>
                {(
                  [
                    { value: "", label: "All" },
                    { value: "AVAILABLE", label: "Available" },
                    { value: "IN_USE", label: "In use" },
                    { value: "RESERVED", label: "Reserved" },
                  ] as const
                ).map(({ value, label }) => (
                  <button
                    key={value || "all"}
                    type="button"
                    className={`py-1.5 px-3.5 rounded-full text-xs font-medium transition-colors border ${
                      statusFilter === value
                        ? "bg-[#fff7ed] text-[#f97316] border-[#fed7aa]"
                        : "border-transparent bg-[#f3f4f6] text-[#6b7280] hover:bg-[#e5e7eb]"
                    }`}
                    onClick={() => setStatusFilter(value)}
                  >
                    {label}
                  </button>
                ))}
                <button
                  type="button"
                  className="hidden sm:inline-flex h-9 items-center justify-center px-4 text-sm font-semibold leading-none text-white bg-orange rounded-lg shrink-0 transition-[transform,box-shadow] duration-[120ms] ease-out hover:translate-y-[-1px] hover:shadow-[0_8px_20px_rgba(0,0,0,0.10)] active:translate-y-0 active:shadow-[0_4px_12px_rgba(0,0,0,0.08)]"
                  onClick={() => setShowCreateModal(true)}
                >
                  Add Table
                </button>
              </div>
            </section>

            {isTableLoading ? (
                  <div className="flex justify-center py-12 flex-1 min-h-[120px]">
                    <BtnLoader />
                  </div>
                ) : displayTables.length > 0 ? (
                  <section
                    className="flex-1 min-h-0 flex flex-col min-w-0 relative"
                    aria-label="Tables list"
                  >
                    <div className="w-full flex-1 min-h-0 flex flex-col min-w-0 overflow-hidden">
                      <div className="flex-1 min-h-0 min-w-0 overflow-x-auto overflow-y-auto overscroll-x-contain">
                        <table
                          className="w-full text-left border-collapse table-fixed"
                          style={{ minWidth: 620 }}
                        >
                          <colgroup>
                            <col style={{ width: "14%" }} />
                            <col style={{ width: "14%" }} />
                            <col style={{ width: "14%" }} />
                            <col style={{ width: "14%" }} />
                            <col style={{ width: "128px" }} />
                            <col style={{ width: "52px" }} />
                          </colgroup>
                          <thead>
                            <tr className="text-[12px] font-semibold uppercase tracking-wide text-[#374151] border-b border-[#e2e8f0]">
                              <th className="sticky top-0 z-10 bg-[#f1f5f9] py-3 pl-4 pr-2 align-middle whitespace-nowrap">
                                <button
                                  type="button"
                                  className="group/btn inline-flex items-center gap-1 hover:text-[#111827] focus:outline-none focus:ring-2 focus:ring-[#f97316] focus:ring-offset-1 rounded"
                                  onClick={() => handleSort("tableNumber")}
                                >
                                  Table #
                                  {sortKey === "tableNumber" ? (
                                    <span className="text-[10px] text-[#9ca3af]" aria-hidden>
                                      {sortOrder === "asc" ? "↑" : "↓"}
                                    </span>
                                  ) : (
                                    <span className="text-[10px] text-[#d1d5db] opacity-0 group-hover/btn:opacity-50 transition-opacity" aria-hidden>↑</span>
                                  )}
                                </button>
                              </th>
                              <th className="sticky top-0 z-10 bg-[#f1f5f9] py-3 px-2 align-middle whitespace-nowrap">
                                <button
                                  type="button"
                                  className="group/btn inline-flex items-center gap-1 hover:text-[#111827] focus:outline-none focus:ring-2 focus:ring-[#f97316] focus:ring-offset-1 rounded"
                                  onClick={() => handleSort("tableSize")}
                                >
                                  Capacity
                                  {sortKey === "tableSize" ? (
                                    <span className="text-[10px] text-[#9ca3af]" aria-hidden>
                                      {sortOrder === "asc" ? "↑" : "↓"}
                                    </span>
                                  ) : (
                                    <span className="text-[10px] text-[#d1d5db] opacity-0 group-hover/btn:opacity-50 transition-opacity" aria-hidden>↑</span>
                                  )}
                                </button>
                              </th>
                              <th className="sticky top-0 z-10 bg-[#f1f5f9] py-3 px-2 align-middle whitespace-nowrap">
                                <button
                                  type="button"
                                  className="group/btn inline-flex items-center gap-1 hover:text-[#111827] focus:outline-none focus:ring-2 focus:ring-[#f97316] focus:ring-offset-1 rounded"
                                  onClick={() => handleSort("section")}
                                >
                                  Section
                                  {sortKey === "section" ? (
                                    <span className="text-[10px] text-[#9ca3af]" aria-hidden>
                                      {sortOrder === "asc" ? "↑" : "↓"}
                                    </span>
                                  ) : (
                                    <span className="text-[10px] text-[#d1d5db] opacity-0 group-hover/btn:opacity-50 transition-opacity" aria-hidden>↑</span>
                                  )}
                                </button>
                              </th>
                              <th className="sticky top-0 z-10 bg-[#f1f5f9] py-3 px-2 align-middle whitespace-nowrap">
                                Shape
                              </th>
                              <th className="sticky top-0 z-10 bg-[#f1f5f9] py-3 px-2 align-middle whitespace-nowrap w-[128px] min-w-[128px]">
                                Status
                              </th>
                              <th className="sticky top-0 z-10 bg-[#f1f5f9] py-3 pl-2 pr-4 align-middle text-right whitespace-nowrap w-[72px]">
                                Actions
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {sortedDisplayTables.map((table: any, index: number) => (
                              <tr
                                key={table.correlationId ?? index}
                                className="group h-12 transition-[background-color] duration-150 ease-out hover:bg-[#f9fafb] cursor-pointer"
                                style={{ borderBottom: "1px solid #f1f5f9" }}
                                onClick={() => magnageTableOrder(table)}
                              >
                                <td className="py-0 pl-4 pr-3 align-middle overflow-hidden">
                                  <div className="min-w-0">
                                    <div className="text-sm font-medium text-gray-900 truncate">
                                      {table.tableNumber ?? "-"}
                                    </div>
                                  </div>
                                </td>
                                <td className="py-0 px-3 align-middle overflow-hidden">
                                  <div className="text-sm text-gray-600 truncate">
                                    {table.tableSize ?? "-"}
                                  </div>
                                </td>
                                <td className="py-0 px-3 align-middle overflow-hidden">
                                  <div className="text-sm text-gray-600 truncate">
                                    {table.section != null && table.section !== "" ? table.section : "-"}
                                  </div>
                                </td>
                                <td className="py-0 px-3 align-middle overflow-hidden">
                                  <div className="text-sm text-gray-600 truncate">
                                    {layoutShapeTypeLabel(table)}
                                  </div>
                                </td>
                                <td className="py-0 px-3 align-middle w-[128px] min-w-[128px]">
                                  <span
                                    className={`inline-block whitespace-nowrap text-xs font-medium px-2.5 py-1 rounded-full ${getStatusPillClass(
                                      table?.status ?? ""
                                    )}`}
                                  >
                                    {getStatusLabel(table?.status ?? "")}
                                  </span>
                                </td>
                                <td
                                  className="py-0 pl-3 pr-4 align-middle"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <div className="flex items-center justify-end">
                                    <TableRowActions
                                      table={table}
                                      onEdit={openEditModal}
                                      onDelete={(t) => handleDeleteClick(t.correlationId)}
                                    />
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </section>
                ) : (
                  <div className="flex flex-col items-center justify-center py-12 text-[#666666] text-sm flex-1">
                    <span>
                      {statusFilter && filteredTables?.length > 0
                        ? "No tables match the current filter."
                        : "No tables. Add one."}
                    </span>
                    {!statusFilter && (
                      <button
                        type="button"
                        className="mt-3 px-4 py-2 rounded-xl bg-[#FD7657] text-white text-sm font-medium hover:opacity-95"
                        onClick={() => setShowCreateModal(true)}
                      >
                        Add table
                      </button>
                    )}
                  </div>
                )}
          </div>
        </div>
      )}

      {activeTab === "layout" && (
        <div className="w-full pb-4">
          <div className="flex items-center justify-between mb-5 flex-wrap gap-2">
            <div className="flex items-center gap-2 flex-wrap">
              {layoutSections.length > 1 && (
                <div className="flex items-center gap-1">
                  {layoutSections.map((sec) => (
                    <button
                      key={sec === null ? "__none__" : sec}
                      type="button"
                      className={`px-3 py-1.5 rounded-xl text-sm font-medium ${
                        selectedLayoutSection === sec
                          ? "bg-[#f97316] border border-[#f97316] text-white font-semibold"
                          : "bg-[#f3f4f6] border border-transparent text-[#6b7280] hover:bg-[#e5e7eb]/80"
                      }`}
                      onClick={() => setSelectedLayoutSection(sec)}
                    >
                      {sec === null ? "No section" : `Section ${sec}`}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div className="flex items-center gap-2">
              {layoutEditMode && (
                <>
                  <div className="flex items-center gap-1.5 relative group/editing mr-1">
                    <span
                      className="inline-flex items-center px-1.5 py-0.5 rounded-lg text-[11px] font-medium border-0"
                      style={{ backgroundColor: "rgba(255, 120, 60, 0.1)", color: "#b45309" }}
                    >
                      Editing Mode
                    </span>
                    <span
                      className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-gray-200/80 text-gray-500 hover:bg-gray-300/80 cursor-help"
                      aria-describedby="layout-instructions-tooltip"
                      aria-label="Layout instructions"
                      role="img"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-3 h-3" aria-hidden>
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                      </svg>
                    </span>
                    <div
                      id="layout-instructions-tooltip"
                      role="tooltip"
                      className="absolute right-0 top-full mt-1.5 hidden group-hover/editing:block z-30 w-56 py-2.5 px-3 bg-white rounded-xl border border-[#F1F1F2] shadow-[0_4px_12px_rgba(0,0,0,0.08)]"
                    >
                      <p className="text-[13px] text-[#525252] leading-relaxed">
                        Drag tables to move.<br />
                        Drag background to pan.<br />
                        Ctrl+scroll (or Cmd+scroll) to zoom.
                      </p>
                    </div>
                  </div>
                  <button
                  type="button"
                  className="px-4 py-2 rounded-xl text-sm font-medium border border-[#e5e7eb] bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                  onClick={handleSaveLayout}
                  disabled={layoutSaving || !layoutDirty}
                  title={!layoutDirty ? "Change shape, move, or rotate a table to save changes" : undefined}
                >
                  {layoutSaving ? "Saving…" : "Save layout"}
                </button>
                </>
              )}
              {layoutEditMode && selectedLayoutItemCorrelationId && (
                <div className="flex flex-wrap items-center gap-1.5">
                  <div className="flex flex-wrap items-center gap-1" role="group" aria-label="Table shape">
                    {(["CIRCLE", "SQUARE", "RECTANGLE"] as const).map((s) => {
                      const selectedShape = layoutItems.find((i) => i.correlationId === selectedLayoutItemCorrelationId)?.shape;
                      const active = selectedShape === s;
                      const label = s === "CIRCLE" ? "Circle" : s === "SQUARE" ? "Square" : "Rectangle";
                      return (
                        <button
                          key={s}
                          type="button"
                          aria-label={label}
                          aria-pressed={active}
                          title={label}
                          className={`inline-flex h-10 min-h-[40px] w-10 min-w-[40px] shrink-0 items-center justify-center rounded-xl border text-[17px] leading-none transition-colors ${
                            active
                              ? "border-[#FD7657] bg-[#fef3ef] text-[#9a3412]"
                              : "border-[#E5E7EB] bg-white text-[#111827] hover:bg-[#fafafa]"
                          } focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#FD7657]/40 focus-visible:border-[#FD7657]`}
                          onClick={() => handleApplyLayoutShape(s)}
                        >
                          {s === "CIRCLE" ? "○" : s === "SQUARE" ? "▢" : "▭"}
                        </button>
                      );
                    })}
                  </div>
                  <button
                    type="button"
                    className="px-4 py-2 rounded-xl text-sm font-medium border border-[#e5e7eb] bg-white text-gray-700 hover:bg-gray-50 h-10 min-h-[40px]"
                    onClick={handleRotate90}
                    aria-label="Rotate selected table 90°"
                  >
                    Rotate 90°
                  </button>
                </div>
              )}
              <button
                type="button"
                className="px-4 py-2 rounded-xl text-sm font-medium bg-[#FD7657] text-white hover:opacity-95 border-0"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  if (layoutEditMode) {
                    setLayoutEditMode(false);
                    setSelectedLayoutItemCorrelationId(null);
                  } else {
                    setLayoutEditMode(true);
                  }
                }}
                aria-label={layoutEditMode ? "Exit edit mode (does not save)" : "Edit layout"}
              >
                {layoutEditMode ? "Done Editing" : "Edit layout"}
              </button>
            </div>
          </div>
          <div className="relative w-full min-h-[600px] max-h-[70vh] rounded-2xl pt-4">
            <div
              ref={viewportRef}
              className="overflow-auto rounded-2xl min-h-[600px] max-h-[70vh] touch-pan-x touch-pan-y w-full"
              style={{ backgroundColor: "#e5e7eb" }}
              aria-label="Layout canvas viewport"
              onWheel={handleLayoutWheel}
            >
              <div style={{ width: canvasSize.w * zoomLevel, height: canvasSize.h * zoomLevel, minWidth: canvasSize.w * zoomLevel, minHeight: canvasSize.h * zoomLevel }}>
              <div
                ref={canvasRef}
                className="relative p-10 transition-colors duration-150"
                style={{
                  width: canvasSize.w,
                  height: canvasSize.h,
                  transform: `scale(${zoomLevel})`,
                  transformOrigin: "0 0",
                  ...(layoutEditMode
                    ? {
                        // Keep canvas transparent so viewport background color is visible.
                        backgroundColor: "transparent",
                        backgroundImage: "radial-gradient(rgba(148,163,184,0.35) 0.5px, transparent 0.5px)",
                        backgroundSize: "24px 24px",
                        cursor: isPanning ? "grabbing" : draggingTableId ? "grabbing" : "grab",
                      }
                    : { backgroundColor: "transparent" }),
                }}
                onPointerDown={handleCanvasBackgroundPointerDown}
                onPointerMove={handleLayoutPointerMove}
                onPointerUp={handleLayoutPointerUp}
                onPointerLeave={(e) => {
                  handleLayoutPointerUp(e);
                  setHoveredTableId(null);
                }}
              >
            {layoutItems.length === 0 ? (
              <div className="flex flex-col items-center justify-center min-h-[560px] text-[#6b7280] text-sm">
                <span>No tables in layout. Add tables in List view.</span>
              </div>
            ) : (
              layoutItems.map((item) => {
                const { width: w, height: h } = getItemLayoutSize(item);
                const shapeClass = item.shape === "CIRCLE" ? "rounded-full" : "rounded-[14px]";
                const rot = item.rotation ?? 0;
                const { layoutX, layoutY } = visualTopLeftToLayout(item.x, item.y, w, h, rot);
                const textColorClass =
                  item.status === "IN_USE"
                    ? "text-gray-800"
                    : item.status === "AVAILABLE"
                    ? "text-[#14532d]"
                    : item.status === "RESERVED"
                    ? "text-[#ea580c]"
                    : "text-[#111827]";
                const isDragging = draggingTableId === item.correlationId;
                const isHovering = hoveredTableId === item.correlationId && !isDragging;
                const scale = isDragging || (layoutEditMode && isHovering) ? 1.02 : 1;
                const transform =
                  rot
                    ? `rotate(${rot}deg) scale(${scale})`
                    : scale !== 1
                    ? `scale(${scale})`
                    : undefined;
                return (
                  <div
                    key={item.correlationId}
                    data-table-card
                    className={`group absolute flex flex-col items-center justify-center overflow-hidden ${shapeClass} ${layoutBorderClass(
                      item.status
                    )} ${layoutFillClass(item.status)} ${layoutEditMode ? (isDragging ? "cursor-grabbing" : "cursor-move") : ""} transition-[left,top,transform,box-shadow] duration-150 ease-out ${
                      isDragging
                        ? "shadow-[0_10px_30px_rgba(0,0,0,0.15)] z-[30]"
                        : layoutEditMode && selectedLayoutItemCorrelationId === item.correlationId
                        ? "shadow-[0_2px_8px_rgba(0,0,0,0.05)] hover:shadow-[0_6px_16px_rgba(0,0,0,0.08)] z-[20]"
                        : "shadow-[0_2px_8px_rgba(0,0,0,0.05)] hover:shadow-[0_6px_16px_rgba(0,0,0,0.08)]"
                    }`}
                    style={{
                      width: w,
                      height: h,
                      minWidth: w,
                      minHeight: h,
                      left: layoutX,
                      top: layoutY,
                      transform,
                      transformOrigin: "50% 50%",
                    }}
                    onPointerDown={(e) => handleLayoutPointerDown(e, item)}
                    onMouseEnter={() => setHoveredTableId(item.correlationId)}
                    onMouseLeave={() => setHoveredTableId((id) => (id === item.correlationId ? null : id))}
                  >
                    <div
                      className="absolute inset-0 flex flex-col pointer-events-none overflow-hidden px-1 pt-1 pb-1.5 min-h-0"
                      style={{
                        borderRadius: "inherit",
                        transform: rot ? `rotate(${-rot}deg)` : undefined,
                      }}
                      aria-hidden
                    >
                      <div className="flex flex-col items-center justify-start gap-0.5 shrink-0 w-full min-w-0">
                        <span className={`text-sm font-semibold leading-none ${textColorClass}`}>T{item.tableNumber}</span>
                        <span
                          className={`text-[9px] font-normal px-1 py-0.5 rounded leading-tight text-center max-w-full ${layoutStatusPillClass(
                            item.status
                          )}`}
                        >
                          {layoutStatusLabel(item.status)}
                        </span>
                      </div>
                      <div className="flex-1 flex flex-col items-center justify-start gap-px min-h-0 min-w-0 w-full pt-0.5 overflow-hidden">
                      {item.status === "IN_USE" && item.currentOrderCorrelationId ? (() => {
                        const order = ordersByCorrelationId.get(item.currentOrderCorrelationId!);
                        if (!order) return <span className={`text-[11px] leading-tight ${textColorClass} opacity-80`}>{item.tableSize} seats</span>;
                        const partySize = order.partySize ?? (order as any).party_size;
                        const seatsText =
                          partySize != null
                            ? `${partySize} / ${item.tableSize} seats`
                            : `? / ${item.tableSize} seats`;
                        const serverName = [order.serverFirstName, order.serverLastName].filter(Boolean).join(" ").trim() || "";
                        return (
                          <>
                            <span className={`text-[11px] font-medium leading-tight text-center px-0.5 ${textColorClass} opacity-80`}>{seatsText}</span>
                            {order.createdAt != null && (
                              <span className="text-[11px] font-normal tabular-nums leading-tight text-gray-800" title="Elapsed time">{formatElapsed(order.createdAt)}</span>
                            )}
                            {order.totalCents != null && (
                              <span className={`text-[10px] font-medium leading-tight ${textColorClass} opacity-90`}>{formatCents(order.totalCents)}</span>
                            )}
                            {serverName ? (
                              <span className="text-[9px] font-light leading-snug text-gray-500 break-words text-center w-full px-0.5 pb-px shrink-0">
                                {serverName}
                              </span>
                            ) : null}
                          </>
                        );
                      })() : (
                        <span className={`text-[11px] leading-tight ${textColorClass} opacity-80`}>{item.tableSize} seats</span>
                      )}
                      </div>
                    </div>
                    {layoutEditMode && (
                      <button
                        type="button"
                        className="absolute -top-1 -right-1 w-6 h-6 rounded-full bg-red-500 text-white text-xs flex items-center justify-center opacity-0 group-hover:opacity-100 hover:opacity-100 transition-opacity shadow pointer-events-auto z-10"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteClick(item.correlationId);
                        }}
                        aria-label="Delete table"
                      >
                        ×
                      </button>
                    )}
                  </div>
                );
              })
            )}
              </div>
            </div>
            </div>
            <div
              className="absolute top-3 right-3 z-20 flex items-center gap-0.5 rounded-xl bg-gray-800/35 backdrop-blur-[6px] py-0.5 px-0.5"
              style={{ border: "1px solid rgba(255,255,255,0.02)", boxShadow: "0 1px 1px rgba(0,0,0,0.02)" }}
            >
              <button
                type="button"
                className="w-5 h-5 flex items-center justify-center rounded text-gray-300 hover:bg-gray-700/80 hover:text-white disabled:opacity-40 disabled:pointer-events-none font-medium text-xs leading-none"
                onClick={zoomOut}
                disabled={zoomLevel <= 0.5}
                aria-label="Zoom out"
                title="Zoom out"
              >
                −
              </button>
              <button
                type="button"
                className="min-w-[1.75rem] h-5 px-0.5 flex items-center justify-center rounded text-gray-400 text-[10px] font-medium hover:text-white hover:bg-gray-700/80"
                onClick={zoomReset}
                aria-label="Reset zoom to 100%"
                title="Reset zoom"
              >
                {Math.round(zoomLevel * 100)}%
              </button>
              <button
                type="button"
                className="w-5 h-5 flex items-center justify-center rounded text-gray-300 hover:bg-gray-700/80 hover:text-white disabled:opacity-40 disabled:pointer-events-none font-medium text-xs leading-none"
                onClick={zoomIn}
                disabled={zoomLevel >= 2}
                aria-label="Zoom in"
                title="Zoom in"
              >
                +
              </button>
              <div className="w-px h-3.5 bg-gray-600/60 mx-0.5" aria-hidden />
              <button
                type="button"
                className="h-5 px-1.5 flex items-center justify-center rounded text-gray-400 hover:text-white text-[10px] font-medium gap-0.5 focus:outline-none focus-visible:ring-1 focus-visible:ring-gray-500 focus-visible:ring-offset-1 focus-visible:ring-offset-gray-800"
                onClick={(e) => {
                  resetView();
                  (e.currentTarget as HTMLButtonElement).blur();
                }}
                aria-label="Reset view (zoom and pan)"
                title="Reset view"
              >
                <span aria-hidden>⟲</span>
                Reset view
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Hidden QR used for per-row Download PNG */}
      {downloadTableToken && getTableQrUrl(downloadTableToken.publicToken) && (
        <div
          ref={hiddenExportQrRef}
          style={{ position: "absolute", left: -9999, top: 0, width: 256, height: 256 }}
          aria-hidden
        >
          <QRCode
            value={getTableQrUrl(downloadTableToken.publicToken)!}
            size={256}
            quietZone={8}
          />
        </div>
      )}

      {/* View QR modal */}
      {viewQrTable && (() => {
        const qrUrl = getTableQrUrl(viewQrTable.publicToken);
        const tableName = viewQrTable.tableNumber != null ? `Table ${viewQrTable.tableNumber}` : "Table";
        const copyLink = () => {
          if (qrUrl) {
            navigator.clipboard.writeText(qrUrl);
            successToast("Link copied to clipboard");
          }
        };
        const downloadModalQr = () => {
          const canvas = viewQrModalQrRef.current?.querySelector("canvas");
          if (canvas) {
            const a = document.createElement("a");
            a.download = `table-${viewQrTable.tableNumber ?? "qr"}-qr.png`;
            a.href = getStyledQrCardDataUrl(canvas, viewQrTable.tableNumber ?? 0);
            a.click();
          }
        };
        return (
          <div
            className="fixed inset-0 z-50 overflow-y-auto"
            aria-modal="true"
            role="dialog"
            aria-labelledby="view-qr-modal-title"
          >
            <div className="fixed inset-0 bg-black/60" aria-hidden="true" onClick={() => setViewQrTable(null)} />
            <div className="flex min-h-full items-center justify-center p-4">
              <div className="relative w-full max-w-md rounded-xl bg-white shadow-xl">
                <div className="p-6">
                  <h2 id="view-qr-modal-title" className="text-lg font-semibold text-gray-900 mb-1">
                    {tableName}
                  </h2>
                  <p className="text-sm text-gray-500 mb-4">Scan to order at this table.</p>
                  <div ref={viewQrModalQrRef} className="flex justify-center mb-4">
                    {qrUrl && (
                      <QRCode value={qrUrl} size={220} quietZone={8} />
                    )}
                  </div>
                  {qrUrl && (
                    <p className="text-xs text-gray-500 break-all mb-4 text-center font-mono">
                      {qrUrl}
                    </p>
                  )}
                  <div className="flex flex-wrap gap-2 justify-center">
                    <button
                      type="button"
                      className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                      onClick={copyLink}
                    >
                      Copy Link
                    </button>
                    <button
                      type="button"
                      className="px-4 py-2 text-sm font-medium text-white bg-orange rounded-lg hover:opacity-95"
                      onClick={downloadModalQr}
                    >
                      Download PNG
                    </button>
                    <button
                      type="button"
                      className="px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
                      onClick={() => setViewQrTable(null)}
                    >
                      Close
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      {showCreateModal || !!editTableModal ? (
        <ManageTableModal
          title={!!editTableModal ? "Edit Table" : "Create Table"}
          setShowCreateModal={() =>
            !!editTableModal ? setEditTableModal(null) : setShowCreateModal(false)
          }
          editTableModal={editTableModal}
        />
      ) : null}

      {activeTab === "guestOrdering" && (
        <div className="w-full flex-1 min-w-0 flex flex-col">
          <div
            className="bg-white rounded-xl shadow-[0_4px_12px_rgba(0,0,0,0.04)] overflow-hidden flex-1 flex flex-col min-h-0"
            style={{ border: `1px solid ${BORDER_LIGHT}` }}
          >
            <section
              className="shrink-0 py-3 px-4 border-b flex flex-row flex-wrap items-center justify-between gap-2"
              style={{ borderColor: BORDER_LIGHT }}
              aria-label="Guest ordering toolbar"
            >
              <p className="text-sm text-[#6b7280]">
                Generate QR codes for customers to scan at tables.
              </p>
              <button
                type="button"
                className="h-9 px-3 text-sm font-semibold text-white bg-orange rounded-lg shrink-0 transition-[transform,box-shadow] duration-[120ms] ease-out hover:translate-y-[-1px] hover:shadow-[0_8px_20px_rgba(0,0,0,0.10)] active:translate-y-0 disabled:opacity-70 disabled:pointer-events-none"
                onClick={downloadAllQrsPdf}
                disabled={downloadAllQrsPdfLoading}
                id="download-all-qrs-pdf-btn"
              >
                {downloadAllQrsPdfLoading ? "Generating…" : "Download All QRs (PDF)"}
              </button>
            </section>
            {isTableLoading ? (
              <div className="flex justify-center py-12 flex-1 min-h-[120px]">
                <BtnLoader />
              </div>
            ) : (filteredTables?.length ?? 0) > 0 ? (
              <div className="flex-1 min-h-0 overflow-auto">
                <table className="w-full text-left border-collapse" style={{ minWidth: 520 }}>
                  <colgroup>
                    <col style={{ width: "14%" }} />
                    <col style={{ width: "12%" }} />
                    <col style={{ width: "12%" }} />
                    <col style={{ width: "100px" }} />
                    <col style={{ width: "140px" }} />
                  </colgroup>
                  <thead>
                    <tr className="bg-[#f1f5f9] text-[12px] font-semibold uppercase tracking-wide text-[#374151] border-b border-[#e2e8f0]">
                      <th className="py-3 pl-4 pr-2">Table</th>
                      <th className="py-3 px-2">Section</th>
                      <th className="py-3 px-2">Seats</th>
                      <th className="py-3 px-2">QR</th>
                      <th className="py-3 pl-2 pr-4 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[...(filteredTables || [])]
                      .sort((a: any, b: any) => (a.tableNumber ?? 0) - (b.tableNumber ?? 0))
                      .map((table: any, index: number) => {
                        const qrUrl = getTableQrUrl(table.publicToken);
                        return (
                          <tr
                            key={table.correlationId ?? index}
                            className="h-14 border-b border-[#f1f5f9] hover:bg-[#f9fafb]"
                          >
                            <td className="py-2 pl-4 pr-2 align-middle">
                              <span className="text-sm font-medium text-gray-900">
                                {table.tableNumber ?? "-"}
                              </span>
                            </td>
                            <td className="py-2 px-2 align-middle text-sm text-gray-600">
                              {table.section != null && table.section !== "" ? table.section : "-"}
                            </td>
                            <td className="py-2 px-2 align-middle text-sm text-gray-600">
                              {table.tableSize ?? "-"}
                            </td>
                            <td className="py-2 px-2 align-middle">
                              {qrUrl ? (
                                <div className="inline-block w-10 h-10 bg-white border border-[#e5e7eb] rounded overflow-hidden">
                                  <QRCode value={qrUrl} size={40} quietZone={0} />
                                </div>
                              ) : (
                                <span className="text-xs text-gray-400">Generating…</span>
                              )}
                            </td>
                            <td className="py-2 pl-2 pr-4 align-middle text-right">
                              {qrUrl && (
                                <div className="flex items-center justify-end gap-1">
                                  <button
                                    type="button"
                                    className="px-2.5 py-1.5 text-xs font-medium text-[#6b7280] hover:text-[#111827] hover:bg-[#f3f4f6] rounded-lg transition-colors"
                                    onClick={() => setViewQrTable(table)}
                                  >
                                    View QR
                                  </button>
                                  <button
                                    type="button"
                                    className="px-2.5 py-1.5 text-xs font-medium text-[#6b7280] hover:text-[#111827] hover:bg-[#f3f4f6] rounded-lg transition-colors"
                                    onClick={() =>
                                      table.publicToken &&
                                      setDownloadTableToken({
                                        publicToken: table.publicToken,
                                        tableNumber: table.tableNumber ?? 0,
                                      })
                                    }
                                    data-table-index={index}
                                  >
                                    Download
                                  </button>
                                </div>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-12 text-[#666666] text-sm flex-1">
                <span>No tables. Add tables in the List tab first.</span>
              </div>
            )}
          </div>
        </div>
      )}

      {isShowTableOrderModal && (
        <TableOrderMOdal
          tableOrderDetails={tableOrderDetails}
          isShowTableOrderModal={() => setisShowTableOrderModal(false)}
        />
      )}
    </ReportPageLayout>
  );
};

export default RestaurantTable;
